import messagePoster from "./../../messagePoster";
import json from "./../../json";
import http from "../../../http";
import paymentUserAgent from "../../paymentUserAgent";
import { Fingerprint, Promise, queryString, strings } from "@securionpay-internal/commons-js";
import { snakeCase } from "lodash";
import { actionMatches } from "../../../utils/actionMatches";
import { GATEWAY_URL_BASED_ON_IFRAME_LOCATION } from "../../../utils/gatewayUrlBasedOnIframeLocation";
import { Charge } from "../../types/response/Charge";
import { ErrorResponse } from "../../types/response/ErrorResponse";
import { Token } from "../../types/response/Token";
import { MerchantConfig } from "../../types/response/MerchantConfig";
import { PayPalInitiate } from "../../types/response/PayPalInitiate";
import {
    UserActionChargeRetrieveOptions,
    UserActionCreateApplePaySessionOptions,
    UserActionInitiatePayPalOptions,
    UserActionPaymentMethodRetrieveOptions,
    UserActionPaymentMethodUpdateOptions,
    UserActionTokenizeOptions,
    UserActionTokenizeV1Options,
    UserActionType,
    UserActionUpdateTokenOptions
} from "../../types/UserAction";
import { CommonOptions } from "child_process";
import { PaymentMethod } from "../../types/response/PaymentMethod";
import Tracker from "../../../Tracker";
import { ControllerFrameParams } from "../ControllerFrameParams";
import { ImplicitFrameParams } from "../../Frame";

type ControllerAppOptions = ControllerFrameParams & ImplicitFrameParams

export default class ControllerApp {
    private readonly APPLE_PAY_HANDLER_TIMEOUT = 30_000;

    options: ControllerAppOptions;
    frameId: string;
    frames: any;
    requests: any;
    tracker: Tracker;
    _listener: any;
    _fingerprint: Fingerprint;

    constructor(options: ControllerAppOptions) {
        this.options = options;
        this.frameId = window.name;
        this.tracker = new Tracker(options.jsId, paymentUserAgent, options.referrer, options.publicKey, options.merchantId);

        this.frames = {};
        this.requests = {};

        this._listener = null;

        this._setupPostMessage();
        this._sendParentMessage({action: "securionpay-controller-load"});

        this._fingerprint = new Fingerprint();
    }

    _sendParentMessage(message) {
        let frameMessage = {
            frameId: this.frameId,
            controllerId: this.options.controllerId,
            message: message
        };

        messagePoster.parentPostMessage(frameMessage);
    }

    sendMessage(frameId, message) {
        let frameMessage = {
            frameId: frameId,
            controllerId: this.options.controllerId,
            message: message
        };

        this._sendMessage(frameMessage);
    }

    _sendMessage(message) {
        let frame = this.frames[message.frameId];
        if (frame.loaded) {
            messagePoster.framePostMessage(message);
        } else {
            frame.queuedMessages.push(message);
        }
    }

    _setupPostMessage() {
        this._listener = (messageEvent) => {
            let messageEventData = json.tryParse(messageEvent.data);
            if (messageEventData) {
                this._handleMessage(messageEventData);
            }
        };
        window.addEventListener("message", this._listener);
    }

    _handleMessage(messageEventData) {
        let frameId = messageEventData.frameId;
        let message = messageEventData.message;

        if (actionMatches(message, 'user-action')) {
            let type = message.payload.type;
            let requestId = message.payload.requestId;
            let options = message.payload.options;
            this.action(type, options).then((resolve) => {
                this._sendParentMessage({
                    action: "securionpay-user-action-complete",
                    payload: {requestId, result: resolve}
                });
            }, (reject) => {
                this._sendParentMessage({
                    action: "securionpay-user-action-error",
                    payload: {requestId, result: reject}
                });
            });

        } else if (actionMatches(message, 'controller-request-complete')) {
            let result = message.payload.result;
            let error = message.payload.error;
            let requestId = message.payload.requestId;
            let request = this.requests[requestId];

            if (result) {
                request.resolve({result, frameId});
            }

            if (error) {
                request.reject({error, frameId});
            }

            delete this.requests[requestId];

        } else if (actionMatches(message, 'user-createframe')) {
            this.frames[message.payload.newFrameId] = {loaded: false, queuedMessages: []};

        } else if (actionMatches(message, 'frame-load')) {
            let frameId = message.payload.loadedFrameId;
            let frame = this.frames[frameId];
            this.frames[frameId] = {...frame, queuedMessages: [], loaded: true};

            frame.queuedMessages.forEach((queuedMessage) => this._sendMessage(queuedMessage));
        }

        if (message.broadcast) {
            Object.keys(this.frames).forEach(frameId => this.sendMessage(frameId, message));
        }
    }

    action(type: UserActionType.TOKENIZE, options: UserActionTokenizeOptions): Promise<Token | ErrorResponse>
    action(type: UserActionType.TOKENIZE_V1, options: UserActionTokenizeV1Options): Promise<Token | ErrorResponse>
    action(type: UserActionType.CHARGE_RETRIEVE, options: UserActionChargeRetrieveOptions): Promise<Charge | ErrorResponse>
    action(type: UserActionType.PAYMENT_METHOD_RETRIEVE, options: UserActionPaymentMethodRetrieveOptions): Promise<PaymentMethod | ErrorResponse>
    action(type: UserActionType.CREATE_APPLE_PAY_SESSION, options: UserActionCreateApplePaySessionOptions): Promise<any | ErrorResponse>
    action(type: UserActionType.INITIATE_PAYPAL, options: UserActionInitiatePayPalOptions): Promise<PayPalInitiate | ErrorResponse>
    action(type: UserActionType.GET_MERCHANT_CONFIG, options: CommonOptions): Promise<MerchantConfig | ErrorResponse>
    action(type: UserActionType.UPDATE_TOKEN, options: UserActionUpdateTokenOptions): Promise<MerchantConfig | ErrorResponse>

    action(type, options) {
        if (type === UserActionType.TOKENIZE) {
            return this.tokenizeAction(options);
        }
        if (type === UserActionType.TOKENIZE_V1) {
            return this.tokenizeV1Action(options);
        }
        if (type === UserActionType.CHARGE_RETRIEVE) {
            return this.retrieveChargeAction(options);
        }
        if (type === UserActionType.PAYMENT_METHOD_RETRIEVE) {
            return this.retrievePaymentMethodAction(options);
        }
        if (type === UserActionType.PAYMENT_METHOD_UPDATE) {
            return this.updatePaymentMethodAction(options);
        }
        if (type === UserActionType.CREATE_APPLE_PAY_SESSION) {
            return this.createApplePaySessionAction(options);
        }
        if (type === UserActionType.INITIATE_PAYPAL) {
            return this.initiatePayPalAction(options);
        }
        if (type === UserActionType.GET_MERCHANT_CONFIG) {
            return this.getMerchantConfigAction();
        }
        if (type === UserActionType.UPDATE_TOKEN) {
            return this.updateTokenAction(options);
        }
        console.error("Unknown action: ", type)
    }

    private tokenizeAction(options: UserActionTokenizeOptions) {
        return this.requestStateFromFrames(Object.keys(this.frames)).then((states) => {
            let mergedResult = {};
            for (let i = 0; i < states.length; i++) {
                let result = states[i].result;
                let error = toErrorIfNeeded(result);
                if (error) {
                    return {error};
                }

                mergedResult[result.componentName] = result.value;
            }

            return mergedResult;
        }).then((data) => {
            if (data.error) {
                return data;
            }

            const request = {
                ...options.data,
                number: sanitizeCardValue(data.number),
                cvc: sanitizeCardValue(data.cvc),
                expMonth: sanitizeCardValue(data.expiryMonth),
                expYear: sanitizeCardValue(data.expiryYear),
                referrer: this.options.referrer,
                paymentUserAgent,
                key: options.publicKey || this.options.publicKey
            };

            if (this.options.merchantId) {
                request['merchant'] = this.options.merchantId
            }
            if (this.options.merchantAccountId && !data.merchantAccountId) {
                request['merchantAccountId'] = this.options.merchantAccountId
            }
            if (data.card) {
                request["number"] = sanitizeCardValue(data.card.number);
                request["cvc"] = sanitizeCardValue(data.card.cvc);

                if (data.card.expiry) {
                    request["expMonth"] = sanitizeCardValue(data.card.expiry.month);
                    request["expYear"] = sanitizeCardValue(data.card.expiry.year);
                }
            }

            if (data.expiry) {
                request["expMonth"] = sanitizeCardValue(data.expiry.month);
                request["expYear"] = sanitizeCardValue(data.expiry.year);
            }

            return this._fingerprint.computeFingerprintHeaders(request.number)
                .then(fingerprintHeaders => {
                    const finalRequest = {...request, ...fingerprintHeaders};
                    return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/tokens`, finalRequest, this.tracker);
                });
        });
    }

    private tokenizeV1Action(options: UserActionTokenizeV1Options) {
        return this._fingerprint.computeFingerprintHeaders(options.request.number)
            .then(fingerprintHeaders => {
                const request = {
                    referrer: this.options.referrer,
                    ...options.request,
                    key: options.publicKey || this.options.publicKey,
                    ...fingerprintHeaders
                };

                return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/tokens`, request, this.tracker);
            });
    }

    private retrieveChargeAction(options: UserActionChargeRetrieveOptions) {
        return http.getWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/charges/${options.clientObjectId}?${this.getAuthenticationParams()}`, this.tracker);
    }

    private retrievePaymentMethodAction(options: UserActionPaymentMethodRetrieveOptions) {
        return http.getWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/payment-methods/${options.clientObjectId}?${this.getAuthenticationParams()}`, this.tracker);
    }

    private updatePaymentMethodAction(options: UserActionPaymentMethodUpdateOptions) {
        return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/payment-methods/${options.clientObjectId}?${this.getAuthenticationParams()}`, options.payload, this.tracker);
    }

    private getAuthenticationParams(additionalParams: Record<string, any> = {}): string {
        const params = {
            ...additionalParams,
            key: this.options.publicKey,
        }
        if (this.options.merchantId) {
            params['merchant'] = this.options.merchantId
        }
        return queryString.stringify(params)
    }

    private createApplePaySessionAction(options: UserActionCreateApplePaySessionOptions) {
        const request = {
            validationUrl: options.validationUrl,
            referrer: this.options.referrer,
            key: this.options.publicKey,
            merchantIdentifier: options.merchantIdentifier
        };

        if (this.options.merchantId) {
            request['merchant'] = this.options.merchantId
        }
        return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/apple-pay/sessions`, request, this.tracker);
    }

    private initiatePayPalAction(options: UserActionInitiatePayPalOptions) {
        const request = {
            amount: options.amount,
            currency: options.currency,
            referrer: this.options.referrer,
            key: this.options.publicKey
        };

        if (this.options.merchantId) {
            request['merchant'] = this.options.merchantId
        }
        return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/payment-methods/paypal/initiate`, request, this.tracker);
    }

    private updateTokenAction(options: UserActionUpdateTokenOptions) {
        return http.postWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/tokens/${options.tokenId}?${this.getAuthenticationParams()}`,
            options.request,
            this.tracker,
            this.APPLE_PAY_HANDLER_TIMEOUT);
    }

    private getMerchantConfigAction() {
        const additionalParams: Record<string, any> = {};
        if (this.options.merchantAccountId) {
            additionalParams['merchantAccountId'] = this.options.merchantAccountId
        }
        return http.getWithRetries(`${GATEWAY_URL_BASED_ON_IFRAME_LOCATION}/components/config?${this.getAuthenticationParams(additionalParams)}`, this.tracker);
    }

    requestStateFromFrames(frameIds) {
        return Promise.all(frameIds.map(this.requestState.bind(this)))
    }

    requestState(frameId) {
        let requestId = strings.unique("");
        return new Promise((resolve, reject) => {
            let loaded = this.frames[frameId].loaded;
            this.requests[requestId] = {
                resolve,
                reject
            };

            if (loaded) {
                this.sendMessage(frameId, {action: "securionpay-controller-request", payload: {requestId: requestId}});
            } else {
                resolve();
            }
        });
    }

    destroy() {
        window.removeEventListener("message", this._listener);
    }
}

function sanitizeCardValue(value: string | undefined | null) {
    return typeof value === 'string' ? value.trim() : value;
}

function toErrorIfNeeded(result: any) {
    let snakeCaseComponentName = snakeCase(result.componentName);
    let readableComponentName = snakeCaseComponentName.replace(/_/g, ' ');
    if (!result.meta.complete) {
        return {
            type: "validation_error",
            code: "incomplete_" + snakeCaseComponentName,
            message: `The ${readableComponentName} is incomplete.`
        }
    } else if (result.meta.error) {
        return {
            type: "validation_error",
            code: "invalid_" + snakeCaseComponentName,
            message: `The ${readableComponentName} is invalid.`
        }
    }

    return null;
}